/*!
 * Copyright (c) REWE Digital GmbH
 */
import { h } from "@stencil/core";
import { isOutsideEvent } from "@rdss/core";
/**
 * A component for creating a accessible side navigation (side-nav) menu.
 * It supports nested menu items and various navigation directions.
 *
 * @example
 * <vx-side-nav
 *   menu-items={[{ title: 'Home', url: '/' }, { title: 'About', url: '/about' }]}
 *   direction="ltr"
 *   button-icon="menu" />
 *
 * @part list-item - The list item for each menu item.
 * @part toggle-icon - The icon button used to toggle the side-nav.
 * @part nav-container - The container for the navigation menu.
 * @part active-list-item - The active list item.
 * @part active-link - The active link.
 * @part heading - The heading for each menu item.
 * @part menu-item - The menu item link.
 */
// eslint-disable-next-line require-jsdoc
export class VxSideNav {
    constructor() {
        /**
         * The custom css prop specified the nav height.
         */
        this.navHeightProp = '--vx-side-nav-height';
        /**
         * Toggles the open state of the side-nav.
         */
        this.toggleMenu = () => (this.isOpen = !this.isOpen);
        /**
         * Expands any menu items that are marked as active based on the current URL path.
         */
        this.expandActiveMenuItems = (menuItems = this.menuItems) => menuItems
            .filter(item => item.active)
            .map((item) => {
            var _a;
            this.toggleMenuItem(item);
            if (((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                this.expandActiveMenuItems(item.children);
            }
        });
        /**
         * Determines if a given menu item is active based on the current URL path.
         *
         * @param {MenuItem} item - The menu item to check.
         */
        this.isActiveMenuItem = (item) => {
            var _a, _b;
            return ((_a = item === null || item === void 0 ? void 0 : item.categoryIds) === null || _a === void 0 ? void 0 : _a.includes(this.categoryId)) || location.pathname === item.url ||
                ((_b = item.children) === null || _b === void 0 ? void 0 : _b.filter(item => this.isActiveMenuItem(item)).length) > 0;
        };
        this.menuItems = [];
        this.categoryId = undefined;
        this.direction = 'ltr';
        this.displayOverlay = true;
        this.buttonIcon = 'menu';
        this.collapseInactiveSiblings = true;
        this.desktopBreakpoint = 1024;
        this.snippets = {
            navigationLabel: 'Hauptnavigation',
            toggleButtonLabel: 'Menü anzeigen',
        };
        this.isOpen = false;
        this.isDesktopViewport = false;
        this.openItems = {};
    }
    /**
     * Event hook to handle window resize.
     */
    setViewportState() {
        this.isDesktopViewport = window.innerWidth >= this.desktopBreakpoint;
    }
    /**
     * Listens for window resize events to adjust the height of the navigation dynamically.
     */
    setNavigationHeight() {
        if (!this.menuContent)
            return;
        const globalNavHeight = getComputedStyle(document.documentElement).getPropertyValue(this.navHeightProp);
        const calculatedNavHeight = `${window.innerHeight - this.menuContent.offsetTop}px`;
        this.menuContent.style.setProperty(this.navHeightProp, globalNavHeight.length ? globalNavHeight : calculatedNavHeight);
    }
    /**
     * Listens for touch events to handle swipe to close navigation on 'ltr' direction.
     */
    handleSwipe({ detail }) {
        if (!this.isOpen)
            return;
        const swipeDirection = detail.dir;
        const shouldClose = (this.direction === 'ltr' && swipeDirection === 'left') ||
            (this.direction === 'rtl' && swipeDirection === 'right') ||
            (this.direction === 'ttb' && swipeDirection === 'up') ||
            (this.direction === 'btt' && swipeDirection === 'down');
        if (shouldClose) {
            this.isOpen = false;
        }
    }
    /**
     * Handles touchstart event.
     */
    handleTouchStart(e) {
        if (isOutsideEvent(e, this.host)) {
            this.isOpen = false;
        }
    }
    /**
     * Watch for changes in the 'isOpen' state and update the button icon accordingly.
     */
    handleButtonIcon() {
        this.buttonIcon = this.isOpen ? 'close' : 'menu';
    }
    /**
     * @inheritdoc
     */
    componentWillLoad() {
        this.expandActiveMenuItems();
    }
    /**
     * @inheritDoc
     */
    componentDidLoad() {
        this.setNavigationHeight();
    }
    /**
     * @inheritDoc
     */
    render() {
        var _a, _b;
        return (h("aside", { key: '07d8d047231787ff5f4c9d3c853d5057cee3a5db', class: `side-menu ${this.isOpen ? 'open' : ''}` }, this.displayOverlay && (h("div", { class: "overlay", style: { top: ((_a = this.menuContent) === null || _a === void 0 ? void 0 : _a.offsetTop) + 'px' }, onClick: () => (this.isOpen = false) })), h("vx-icon-button", { key: '5e9e6340380b3ec5e70eee6a03fc4523e28e2d97', part: "toggle-icon", onClick: this.toggleMenu, "aria-label": this.snippets.toggleButtonLabel, icon: this.buttonIcon }), h("nav", { key: 'e6e1866734427507160f3b0a95bd7332b3096c19', part: "nav-container", style: { top: ((_b = this.menuContent) === null || _b === void 0 ? void 0 : _b.offsetTop) + 'px' }, class: `menu-content ${this.direction}`, ref: el => (this.menuContent = el), "aria-label": this.snippets.navigationLabel }, this.renderMenuItems(this.menuItems))));
    }
    /**
     * Renders the provided menu items as a list. Recursively renders children for nested menus.
     *
     * @param {MenuItem[]} items - The menu items to render.
     * @param {string} parentId - The ID of the parent menu item, used for generating unique item IDs.
     */
    renderMenuItems(items, parentId = 'root') {
        return (h("ul", { role: "menu" }, items === null || items === void 0 ? void 0 : items.map((item, index) => {
            var _a, _b;
            item.id = `${parentId}-${index}`;
            item.collapsable = (_a = item.collapsable) !== null && _a !== void 0 ? _a : true;
            item.active = this.isActiveMenuItem(item);
            const { id, active, collapsable, children, naviTrackingLink, title, url } = item;
            const hasChildren = ((_b = item.children) === null || _b === void 0 ? void 0 : _b.length) > 0;
            return (h("li", { role: "presentation", part: active ? 'active-list-item' : 'list-item', class: {
                    open: !collapsable || this.openItems[id],
                    active,
                    collapsable,
                    'has-children': hasChildren,
                    [parentId]: true,
                } }, h("a", { href: url || '#', "navi-tracking-view": this.isDesktopViewport ? 'desktop' : 'mobile', "navi-tracking-link": naviTrackingLink, part: !collapsable && hasChildren ? 'heading' : 'menu-item', role: "menuitem", "aria-expanded": !!this.openItems[id], "aria-current": active ? 'page' : undefined, class: { active }, style: { pointerEvents: location.pathname === item.url && !hasChildren ? 'none' : 'auto' }, onClick: ev => children && this.toggleMenuItem(item, ev) }, h("span", { part: active ? 'active-link' : 'link' }, title)), children && this.renderMenuItems(children, id)));
        })));
    }
    /**
     * Toggles the open state of a specific menu item.
     * Prevents the default action if called as an event handler.
     *
     * @param {MenuItem} item - The menu item to toggle.
     * @param {UIEvent} [event] - The event that triggered the toggle, if any.
     */
    toggleMenuItem(item, event) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        this.openItems = Object.assign(Object.assign({}, this.openItems), { [item.id]: !(this.openItems[item.id]) });
        this.collapseMenuItemSiblings(item);
    }
    /**
     * Collapses all sibling menu items of the specified menu item.
     */
    collapseMenuItemSiblings(item) {
        if (!this.collapseInactiveSiblings)
            return;
        Object.keys(this.openItems)
            .filter(id => item.id && item.id !== id)
            .map(id => (this.openItems[id] = item.id.includes(id)));
    }
    static get is() { return "vx-side-nav"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-side-nav.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-side-nav.css"]
        };
    }
    static get properties() {
        return {
            "menuItems": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItems",
                    "resolved": "MenuItem[]",
                    "references": {
                        "MenuItems": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-side-nav/types/index.ts::MenuItems"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "An array of menu items to display in the side-nav. Each menu item can optionally have children."
                },
                "defaultValue": "[]"
            },
            "categoryId": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The current category ID. Optional."
                },
                "attribute": "category-id",
                "reflect": false
            },
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'ltr' | 'rtl' | 'ttb' | 'btt'",
                    "resolved": "\"btt\" | \"ltr\" | \"rtl\" | \"ttb\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The navigation direction. Can be 'ltr' (left-to-right), 'rtl' (right-to-left), 'ttb' (top-to-bottom),\nor 'btt' (bottom-to-top)."
                },
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "'ltr'"
            },
            "displayOverlay": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Display overlay when menu is open if true. Default is 'true'."
                },
                "attribute": "display-overlay",
                "reflect": false,
                "defaultValue": "true"
            },
            "collapseInactiveSiblings": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean that determines whether sibling menu items of an active or\nopened item should be automatically collapsed."
                },
                "attribute": "collapse-inactive-siblings",
                "reflect": false,
                "defaultValue": "true"
            },
            "desktopBreakpoint": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The desktop breakpoint."
                },
                "attribute": "desktop-breakpoint",
                "reflect": false,
                "defaultValue": "1024"
            },
            "snippets": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "{ navigationLabel: string; toggleButtonLabel: string; }",
                    "resolved": "{ navigationLabel: string; toggleButtonLabel: string; }",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The snippets for the navigation."
                },
                "defaultValue": "{\n        navigationLabel: 'Hauptnavigation',\n        toggleButtonLabel: 'Men\u00FC anzeigen',\n    }"
            }
        };
    }
    static get states() {
        return {
            "buttonIcon": {},
            "isOpen": {},
            "isDesktopViewport": {},
            "openItems": {}
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "isOpen",
                "methodName": "handleButtonIcon"
            }];
    }
    static get listeners() {
        return [{
                "name": "resize",
                "method": "setViewportState",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "resize",
                "method": "setNavigationHeight",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "swiped",
                "method": "handleSwipe",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "touchstart",
                "method": "handleTouchStart",
                "target": "document",
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=vx-side-nav.js.map
